/**
 * Personas Browse Page
 *
 * Searchable, filterable list of personas with:
 * - Search by profile ID
 * - Filter by generation mode
 * - Filter by product context
 * - Pagination
 */

import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, ChevronLeft, ChevronRight, RefreshCw, X } from 'lucide-react'
import { usePersonas, GenerationMode } from '@/hooks/usePersonas'
import PersonaCard, { PersonaCardSkeleton } from '@/components/personas/PersonaCard'

const PRODUCT_CONTEXTS = [
  { value: '', label: 'All Products' },
  { value: 'engage', label: 'Engage' },
  { value: 'coach', label: 'Coach' },
  { value: 'identity', label: 'Identity' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'legal', label: 'Legal' },
  { value: 'talent', label: 'Talent' },
  { value: 'education', label: 'Education' },
  { value: 'finance', label: 'Finance' },
]

const GENERATION_MODES = [
  { value: '', label: 'All Modes' },
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'minimal', label: 'Minimal' },
]

const ITEMS_PER_PAGE = 12

export default function PersonasBrowse() {
  const [search, setSearch] = useState('')
  const [productContext, setProductContext] = useState('')
  const [generationMode, setGenerationMode] = useState<GenerationMode | ''>('')
  const [page, setPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)

  // Build filters object
  const filters = useMemo(
    () => ({
      search: search || undefined,
      product_context: productContext || undefined,
      generation_mode: generationMode || undefined,
      page,
      limit: ITEMS_PER_PAGE,
    }),
    [search, productContext, generationMode, page]
  )

  const { data, isLoading, isFetching, refetch } = usePersonas(filters)

  const personas = data?.personas || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE)

  const hasFilters = search || productContext || generationMode

  const clearFilters = () => {
    setSearch('')
    setProductContext('')
    setGenerationMode('')
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <Link to="/tenant/personas" className="hover:text-personic-600">
              Personas
            </Link>
            <span>/</span>
            <span>Browse</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Browse Personas</h2>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by profile ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500 focus:border-transparent"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg"
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasFilters && (
              <span className="bg-personic-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                !
              </span>
            )}
          </button>

          {/* Desktop Filters */}
          <div className="hidden md:flex gap-4">
            <select
              value={productContext}
              onChange={(e) => {
                setProductContext(e.target.value)
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
            >
              {PRODUCT_CONTEXTS.map((ctx) => (
                <option key={ctx.value} value={ctx.value}>
                  {ctx.label}
                </option>
              ))}
            </select>

            <select
              value={generationMode}
              onChange={(e) => {
                setGenerationMode(e.target.value as GenerationMode | '')
                setPage(1)
              }}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
            >
              {GENERATION_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Mobile Filters Drawer */}
        {showFilters && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-200 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Context
              </label>
              <select
                value={productContext}
                onChange={(e) => {
                  setProductContext(e.target.value)
                  setPage(1)
                }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              >
                {PRODUCT_CONTEXTS.map((ctx) => (
                  <option key={ctx.value} value={ctx.value}>
                    {ctx.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Generation Mode
              </label>
              <select
                value={generationMode}
                onChange={(e) => {
                  setGenerationMode(e.target.value as GenerationMode | '')
                  setPage(1)
                }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              >
                {GENERATION_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="w-full flex items-center justify-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg"
              >
                <X className="w-4 h-4" />
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {total} persona{total !== 1 ? 's' : ''} found
        </span>
        {totalPages > 1 && (
          <span>
            Page {page} of {totalPages}
          </span>
        )}
      </div>

      {/* Personas Grid */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
            <PersonaCardSkeleton key={i} />
          ))}
        </div>
      ) : personas.length === 0 ? (
        <div className="card p-12 text-center">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No personas found</h3>
          <p className="text-gray-500 mb-4">
            {hasFilters
              ? 'Try adjusting your search or filters'
              : 'Generate personas from your profiles to see them here'}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {personas.map((persona) => (
            <PersonaCard key={persona.persona_id} persona={persona} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="flex items-center gap-1 px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (page <= 3) {
                pageNum = i + 1
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = page - 2 + i
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-10 h-10 rounded-lg transition-colors ${
                    page === pageNum
                      ? 'bg-personic-600 text-white'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1 px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
